import { create } from 'zustand';
import { useAuthStore } from './authStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/india`;

function getUserId() {
  return useAuthStore.getState().user?.id || 'default';
}

export const useAlertStore = create((set, get) => ({
  rules: [],
  events: [],
  unreadCount: 0,
  loading: false,
  error: null,
  panelOpen: false,
  _pollTimer: null,

  setPanelOpen: (open) => set({ panelOpen: open }),
  togglePanel: () => set((s) => ({ panelOpen: !s.panelOpen })),

  fetchEvents: async () => {
    const userId = getUserId();
    try {
      const res = await fetch(`${API}/alerts/events?user_id=${userId}&limit=50`);
      if (!res.ok) return;
      const data = await res.json();
      set({ events: data.events || [], unreadCount: data.unread_count || 0 });
    } catch (e) { /* silent */ }
  },

  fetchRules: async () => {
    const userId = getUserId();
    set({ loading: true });
    try {
      const res = await fetch(`${API}/alerts/rules?user_id=${userId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      set({ rules: data.rules || [], loading: false });
    } catch (e) {
      set({ loading: false, error: e.message });
    }
  },

  createRule: async (rule) => {
    const userId = getUserId();
    try {
      const res = await fetch(`${API}/alerts/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, ...rule }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await get().fetchRules();
      return true;
    } catch (e) {
      set({ error: e.message });
      return false;
    }
  },

  toggleRule: async (ruleId, enabled) => {
    try {
      await fetch(`${API}/alerts/rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: enabled }),
      });
      await get().fetchRules();
    } catch (e) { /* silent */ }
  },

  deleteRule: async (ruleId) => {
    try {
      await fetch(`${API}/alerts/rules/${ruleId}`, { method: 'DELETE' });
      await get().fetchRules();
    } catch (e) { /* silent */ }
  },

  markRead: async (eventId) => {
    try {
      await fetch(`${API}/alerts/events/${eventId}/read`, { method: 'PUT' });
      set((s) => ({
        events: s.events.map(e => e.id === eventId ? { ...e, is_read: true } : e),
        unreadCount: Math.max(0, s.unreadCount - 1),
      }));
    } catch (e) { /* silent */ }
  },

  markAllRead: async () => {
    const userId = getUserId();
    try {
      await fetch(`${API}/alerts/events/read-all?user_id=${userId}`, { method: 'PUT' });
      set((s) => ({
        events: s.events.map(e => ({ ...e, is_read: true })),
        unreadCount: 0,
      }));
    } catch (e) { /* silent */ }
  },

  startPolling: (intervalMs = 60000) => {
    const existing = get()._pollTimer;
    if (existing) clearInterval(existing);
    get().fetchEvents();
    const timer = setInterval(() => get().fetchEvents(), intervalMs);
    set({ _pollTimer: timer });
  },

  stopPolling: () => {
    const timer = get()._pollTimer;
    if (timer) clearInterval(timer);
    set({ _pollTimer: null });
  },
}));
